import {AccountPropertyHistory}from './AccountPropertyHistory';
import { ArchiveImportPanel } from './ArchiveImportPanel';
import type { ArchiveMaintenanceDraft } from './archiveImport';
import { PlanImportPanel } from './PlanImportPanel';
import { PropertyHistoryPanel } from './PropertyHistoryPanel';
import { reminderNavigationEvent } from './reminders';
import { exportEvidence } from './evidenceExport';
import { DocumentRemovalPanel } from './DocumentRemovalPanel';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { authFetch, isDelegated } from '../auth';
import { property } from './api';
import { CheckEditor } from './DueDiligencePage';
import { AuthRequired } from './PropertyPages';
import {
  applicable,
  checklistTemplates,
  exampleDossier,
  isClosed,
  newDossier,
  purchaseBlockers,
  stageLabels,
  statusLabels,
  type Document,
  type Dossier,
} from './dueDiligence';
import { CostsPanel, FinancingPanel, TaskPanel } from './PlanningPanels';
import { PortfolioFundingPanel } from './PortfolioFundingPanel';
import { MaintenanceWorkspace } from './MaintenanceWorkspace';
import type { MaintenanceState, MaintenanceSnapshot } from './maintenance';
import {
  costSummary,
  financeProjection,
  newPlanning,
  type Planning,
} from './purchasePlanning';
import { eur, parseProperty } from './types';
import type { WorkspaceContext } from './WorkspaceShell';
import './purchaseWorkspace.css';
import { replacePlanning } from './planningChanges';
import { costRangeLabel } from './costEvidence';
import { RecordingUploader, fileSize } from './RecordingUploader';
import { AnalysisPanel, type AnalysisCapabilities } from './AnalysisPanel';
import {
  guidedTasks,
  isUnresolvedIssue,
  updateGuidedTask,
  type GuidedTask,
} from './guidedTasks';
import {
  analysisConsentBinding,
  analysisServiceLabel,
} from './analysisServices';

type Project = {
  householdId?: string;
  actorId?: string;
  id: string;
  title: string;
  location: string;
  state: Dossier;
  documents: Document[];
  version: number;
  maintenance?: MaintenanceState;
  maintenanceVersion?: number;
};
type Section =
  | 'overview'
  | 'tasks'
  | 'documents'
  | 'costs'
  | 'finance'
  | 'checks'
  | 'ownership';
const sections: [Section, string, string][] = [
  ['overview', 'Dein Überblick', '01'],
  ['tasks', 'Nächste Schritte', '02'],
  ['documents', 'Unterlagen & Besuche', '03'],
  ['costs', 'Kosten verstehen', '04'],
  ['finance', 'Finanzierung planen', '05'],
  ['checks', 'Prüfen & entscheiden', '06'],
  ['ownership', 'Eigentum & Wartung', '07'],
];
function sampleProjects(): Project[] {
  const a = exampleDossier().state;
  a.planning = newPlanning(320000);
  a.planning.finance = {
    ...a.planning.finance,
    equity: 100000,
    retainedReserve: 20000,
    completionYear: 1957,
  };
  a.planning.costs = [
    {
      id: 'tank',
      title: 'Öltank erneuern',
      category: 'repair',
      dueMonth: 0,
      revisions: [
        {
          id: 'tank-first',
          low: 6000,
          high: 12000,
          level: 'allowance',
          documentId: '',
          locator: '',
          note: 'Fiktive Planungsreserve; schriftliche Angebote stehen aus.',
          createdAt: '2026-10-14T10:00:00Z',
        },
      ],
    },
    {
      id: 'moisture',
      title: 'Feuchteursache untersuchen',
      category: 'inspection',
      dueMonth: 0,
      revisions: [],
    },
  ];
  a.planning.tasks = [
    {
      id: 'tank-report',
      title: 'Schriftlichen Tankbefund einholen',
      owner: 'Wir',
      dueDate: '',
      status: 'waiting',
      phase: 'assessment',
      repeatMonths: 0,
      reminderDays: 1,
      documentId: '',
      note: 'Befund, Sanierung und Prüfgrenzen erfragen.',
    },
    {
      id: 'area-confirm',
      title: 'Getrennte Wohnflächen bestätigen',
      owner: 'Architektur',
      dueDate: '',
      status: 'todo',
      phase: 'assessment',
      repeatMonths: 0,
      reminderDays: 1,
      documentId: '',
      note: 'Haupthaus und Einliegerwohnung abgleichen.',
    },
  ];
  a.planning.units = [
    {
      id: 'main',
      title: 'Haupthaus',
      areaM2: 110,
      use: 'private',
      rentMonthly: 0,
      rentalStartMonth: 0,
      rentGrowthPercent: 0,
      vacancyPercent: 0,
      ownerCostsMonthly: 150,
      maintenanceReserveMonthly: 150,
      actualDeductibleCostsMonthly: 0,
      legalRentabilityConfirmed: false,
      documentId: '',
    },
    {
      id: 'annex',
      title: 'Einliegerwohnung',
      areaM2: 55,
      use: 'rental',
      rentMonthly: 600,
      rentalStartMonth: 12,
      rentGrowthPercent: 2,
      vacancyPercent: 3,
      ownerCostsMonthly: 50,
      maintenanceReserveMonthly: 75,
      actualDeductibleCostsMonthly: 0,
      legalRentabilityConfirmed: false,
      documentId: '',
    },
  ];
  const b = newDossier();
  b.profile = { oilHeating: 'no', basement: 'yes', alterations: 'no' };
  b.planning = newPlanning(450000);
  b.planning.finance.equity = 130000;
  b.planning.finance.completionYear = 2004;
  return [
    {
      id: 'sample-a',
      title: 'Haus mit Einliegerwohnung',
      location: 'Fiktives Beispiel · Bestand & Ölheizung',
      state: a,
      documents: [],
      version: 0,
      maintenance: {assets:[],plans:[]}, maintenanceVersion: 0,
    },
    {
      id: 'sample-b',
      title: 'Familienhaus im Vergleich',
      location: 'Fiktives Beispiel · Alternative',
      state: b,
      documents: [],
      version: 0,
      maintenance: {assets:[],plans:[]}, maintenanceVersion: 0,
    },
  ];
}
function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function PurchaseWorkspace() {
  const { user } = useOutletContext<WorkspaceContext>();
  const { listingId } = useParams();
  const [searchParams] = useSearchParams();
  const reminderTask = searchParams.get('reminderTask') ?? '';
  const reminderNavigation = useRef('');
  const demo = !user && !listingId;
  const [projects, setProjects] = useState<Project[]>(() =>
    user || listingId ? [] : sampleProjects(),
  );
  const [selectedId, setSelectedId] = useState(listingId ?? '');
  const [section, setSection] = useState<Section>('overview');
  const [loading, setLoading] = useState(Boolean(user || listingId));
  const [portfolioReload, setPortfolioReload] = useState(0);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [exportingId, setExportingId] = useState('');
  const [historyId, setHistoryId] = useState('');
  const [importId, setImportId] = useState('');
  const [archiveId,setArchiveId]=useState('');
  const [archivePendingIds,setArchivePendingIds]=useState<string[]>([]);
  const [archiveMaintenanceDraft,setArchiveMaintenanceDraft]=useState<ArchiveMaintenanceDraft|null>(null);
  const archivePending=useCallback((id:string,pending:boolean)=>setArchivePendingIds(ids=>pending?ids.includes(id)?ids:[...ids,id]:ids.filter(v=>v!==id)),[]);
  const clearArchiveMaintenance=useCallback(()=>setArchiveMaintenanceDraft(null),[]);
  const [dirtyIds, setDirtyIds] = useState<string[]>([]);
  const [checkId, setCheckId] = useState('area');
  const [maintenanceFocus, setMaintenanceFocus] = useState('');
  const [removalTarget, setRemovalTarget] = useState<Document | null>(null);
  const [removalPendingIds, setRemovalPendingIds] = useState<string[]>([]);
  const [maintenanceDirtyIds, setMaintenanceDirtyIds] = useState<string[]>([]);
  const [maintenanceRefreshKeys, setMaintenanceRefreshKeys] = useState<Record<string, number>>({});
  const removalUserKey = user ? isDelegated(user) ? user.principal.userId : user.profile.sub : 'demo';
  useEffect(() => {
    const timer = setTimeout(() => { setMaintenanceDirtyIds([]); setRemovalPendingIds([]); setArchivePendingIds([]); setArchiveMaintenanceDraft(null); setArchiveId(''); }, 0);
    return () => clearTimeout(timer);
  }, [removalUserKey]);
  useEffect(() => {
    if (!loading && !busy && !dirtyIds.length && !maintenanceDirtyIds.length && !removalPendingIds.length && !archivePendingIds.length) return;
    const block = (event: Event) => event.preventDefault();
    window.addEventListener(reminderNavigationEvent, block);
    return () => window.removeEventListener(reminderNavigationEvent, block);
  }, [loading, busy, dirtyIds, maintenanceDirtyIds, removalPendingIds,archivePendingIds]);
  const removalSession = useRef(user);
  useEffect(() => { removalSession.current = user; }, [user]);
  const removalPending = useCallback((id: string, pending: boolean) => setRemovalPendingIds(ids => pending ? ids.includes(id) ? ids : [...ids, id] : ids.filter(value => value !== id)), []);
  const maintenanceDirty = useCallback((id: string, dirty: boolean) => setMaintenanceDirtyIds(ids => dirty ? ids.includes(id) ? ids : [...ids, id] : ids.filter(value => value !== id)), []);
  const maintenanceSnapshot = useCallback((id: string, maintenance: MaintenanceState, maintenanceVersion?: number) => {
    setProjects(current => current.map(project => project.id === id ? { ...project, maintenance, maintenanceVersion } : project));
  }, []);
  const [capabilities, setCapabilities] = useState<AnalysisCapabilities | null>(
    null,
  );
  const [phase, setPhase] = useState('purchase');
  const files = useRef(new Map<string, File>());
  const [analyseUploadOrigin, setAnalyseUploadOrigin] = useState('');
  const uploadAnalysisConsent = useRef('');
  useEffect(() => {
    if (!user || isDelegated(user)) return;
    let active = true;
    const timer = setTimeout(() => {
      setLoading(true);
      setProjects([]);
      setSelectedId('');
      const portfolio = authFetch(user, '/api/research/purchase-projects').then(
        (data) =>
          data.items.map(
            (item: {
              listingId: string;
              title: string;
              location: string;
              price?: number | null;
              state: Dossier;
              documents?: Document[];
              version: number;
              maintenanceVersion?: number;
              maintenance?: MaintenanceState;
            }) => ({
              id: item.listingId,
              householdId: data.householdId,
              actorId: data.actorId,
              title: item.title ?? 'Objekt',
              location: item.location ?? '',
              state: {
                ...item.state,
                planning:
                  item.state.planning ?? newPlanning(item.price ?? null),
              },
              documents: item.documents ?? [],
              version: item.version,
              maintenance: item.maintenance,
              maintenanceVersion: item.maintenanceVersion,
            }),
          ) as Project[],
      );
      const request = listingId
        ? Promise.all([
            authFetch(
              user,
              `/api/research/properties/${encodeURIComponent(listingId)}/due-diligence`,
            ),
            portfolio,
          ]).then(async ([dossier, others]) => {
            // Recovered names are household metadata; a private target has no
            // public listing version to fetch or populate.
            const listing = typeof dossier.privateTitle==='string'
              ? {title:dossier.privateTitle,location:'',price:null}
              : parseProperty(await property(user,listingId));
            return [
              {
                id: listingId,
                householdId: dossier.householdId,
                actorId: dossier.actorId,
                title: listing.title,
                location: listing.location ?? '',
                state: {
                  ...dossier.state,
                  planning:
                    dossier.state.planning ??
                    newPlanning(listing.price ?? null),
                },
                documents: dossier.documents,
                version: dossier.version,
                maintenance: dossier.maintenance ?? others.find(item => item.id === listingId)?.maintenance,
                maintenanceVersion: dossier.maintenanceVersion ?? others.find(item => item.id === listingId)?.maintenanceVersion,
              },
              ...others.filter((item) => item.id !== listingId),
            ];
          })
        : portfolio;
      request
        .then((value) => {
          if (active) {
            setProjects(value);
            setSelectedId(listingId ?? '');
            setDirtyIds([]);
            setError('');
          }
        })
        .catch(
          (reason) =>
            active &&
            setError(
              reason instanceof Error
                ? reason.message
                : 'Projekte konnten nicht geladen werden.',
            ),
        )
        .finally(() => active && setLoading(false));
    }, 0);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [user, listingId, portfolioReload]);
  useEffect(() => {
    if (!dirtyIds.length) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyIds]);
  useEffect(() => {
    if (!user || isDelegated(user)) return;
    let active = true;
    authFetch(user, '/api/research/analysis-capabilities')
      .then((data) => active && setCapabilities(data))
      .catch(() => active && setCapabilities(null));
    return () => {
      active = false;
    };
  }, [user]);
  const project = projects.find((item) => item.id === selectedId);
  const reloadAfterRemoval = useCallback(async (discard: boolean, scope: { householdId: string; actorId: string }) => {
    if (!user || !project || !discard && (dirtyIds.includes(project.id) || maintenanceDirtyIds.includes(project.id))) throw new Error('Lokale Entwürfe zuerst exportieren und das Laden des gespeicherten Stands ausdrücklich bestätigen.');
    const dossier = await authFetch(user, `/api/research/properties/${encodeURIComponent(project.id)}/due-diligence`);
    if (dossier.householdId !== scope.householdId || dossier.actorId !== scope.actorId) throw new Error('Anmeldung hat gewechselt. Bitte mit der ursprünglichen Anmeldung prüfen.');
    const maintenance: MaintenanceSnapshot | null = dossier.version > 0 ? await authFetch(user, `/api/research/properties/${encodeURIComponent(project.id)}/maintenance`) : null;
    if (removalSession.current !== user || maintenance && maintenance.householdId !== scope.householdId) throw new Error('Anmeldung hat gewechselt. Bitte mit der ursprünglichen Anmeldung prüfen.');
    setProjects(items => items.map(item => item.id === project.id ? { ...item, state: dossier.state, documents: dossier.documents, version: dossier.version, maintenance: maintenance?.state, maintenanceVersion: maintenance?.version } : item));
    setDirtyIds(ids => ids.filter(id => id !== project.id));
    setMaintenanceRefreshKeys(keys => ({ ...keys, [project.id]: (keys[project.id] ?? 0) + 1 }));
  }, [user, project, dirtyIds, maintenanceDirtyIds]);
  const planning = project?.state.planning ?? newPlanning();
  const tasks = project ? guidedTasks(project.state, project.documents, project.maintenance) : [];
  const issues = project
    ? tasks.filter((task) => isUnresolvedIssue(project.state, task))
    : [];
  const firstIssue = issues[0];
  const update = (next: Project) => {
    const blockers = purchaseBlockers(next.state, next.documents);
    const adjusted =
      blockers.length && ['notary', 'handover'].includes(next.state.stage)
        ? { ...next, state: { ...next.state, stage: 'assessment' as const } }
        : next;
    setProjects((current) =>
      current.map((item) => (item.id === next.id ? adjusted : item)),
    );
    setDirtyIds((current) =>
      current.includes(next.id) ? current : [...current, next.id],
    );
    setError('');
    setMessage('');
  };
  const plan = (value: Planning) =>
    project &&
    update({ ...project, state: replacePlanning(project.state, value) });
  const openTaskSource = (task: GuidedTask) => {
    if (task.source === 'maintenance') {
      setMaintenanceFocus(task.sourceId);
      setSection('ownership');
    } else if (task.source === 'check') {
      const template = checklistTemplates.find(
        (item) => item.id === task.sourceId,
      );
      if (template) {
        setCheckId(template.id);
        setPhase(template.phase);
      }
      setSection('checks');
    } else
      setSection(
        task.source === 'cost'
          ? 'costs'
          : task.source === 'finance'
            ? 'finance'
            : 'tasks',
      );
  };
  useEffect(() => {
    if (loading || !project || project.id !== listingId || !reminderTask) return;
    const key = JSON.stringify([listingId, reminderTask]);
    if (reminderNavigation.current === key) return;
    const timer = setTimeout(() => {
      reminderNavigation.current = key;
      const task = guidedTasks(project.state, project.documents, project.maintenance).find(task => task.id === reminderTask);
      if (!task) { setMessage('Die erinnerte Aufgabe ist im aktuellen Objektstand nicht mehr vorhanden. Bitte den gespeicherten Stand prüfen.'); return; }
      if (task.source === 'maintenance') { setMaintenanceFocus(task.sourceId); setSection('ownership'); }
      else if (task.source === 'check') {
        const template = checklistTemplates.find(value => value.id === task.sourceId);
        if (template) { setCheckId(template.id); setPhase(template.phase); }
        setSection('checks');
      } else setSection(task.source === 'cost' ? 'costs' : task.source === 'finance' ? 'finance' : 'tasks');
      setMessage(`Erinnerung geöffnet: ${task.title}. Der aktuelle Objektstand gilt; die Erinnerung erledigt keine Aufgabe.`);
    }, 0);
    return () => clearTimeout(timer);
  }, [loading, project, listingId, reminderTask]);
  const save = async () => {
    if (!project || !user) return;
    setBusy(true);
    try {
      const result = await authFetch(
        user,
        `/api/research/properties/${encodeURIComponent(project.id)}/due-diligence`,
        {
          method: 'PUT',
          body: JSON.stringify({
            expectedVersion: project.version,
            expectedHouseholdId: project.householdId,
            expectedActorId: project.actorId,
            state: project.state,
          }),
        },
      );
      setProjects((current) =>
        current.map((item) =>
          item.id === project.id ? { ...item, version: result.version } : item,
        ),
      );
      setDirtyIds((current) => current.filter((id) => id !== project.id));
      setMessage('Dein Plan ist im Haushalt gespeichert.');
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Speichern fehlgeschlagen.',
      );
    } finally {
      setBusy(false);
    }
  };
  const upload = async (file: File) => {
    if (!project) return;
    setBusy(true);
    try {
      if (file.size > 100 * 1024 * 1024)
        throw new Error(
          'Dateien bis 100 MB; große Aufnahmen vor dem Hochladen komprimieren.',
        );
      let record: Document;
      if (demo) {
        const hash = new Uint8Array(
          await crypto.subtle.digest('SHA-256', await file.arrayBuffer()),
        );
        record = {
          id: crypto.randomUUID(),
          name: file.name,
          mediaType: file.type,
          size: file.size,
          sha256: Array.from(hash, (b) => b.toString(16).padStart(2, '0')).join(
            '',
          ),
        };
        files.current.set(record.id, file);
      } else {
        record = await authFetch(
          user!,
          `/api/research/properties/${encodeURIComponent(project.id)}/due-diligence/documents?${new URLSearchParams({ name: file.name, mediaType: file.type })}`,
          {
            method: 'POST',
            body: file,
            headers: { 'Content-Type': file.type },
          },
        );
      }
      await storedDocument(record);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Upload fehlgeschlagen.',
      );
    } finally {
      setBusy(false);
    }
  };
  const storedDocument = async (record: Document) => {
    if (!project) return;
    setProjects((current) =>
      current.map((item) =>
        item.id === project.id
          ? {
              ...item,
              documents: item.documents.some(
                (document) => document.id === record.id,
              )
                ? item.documents
                : [...item.documents, record],
            }
          : item,
      ),
    );
    setMessage(
      'Dokument hinzugefügt. Verknüpfe den Befund mit einer Prüfung oder Kostenposition.',
    );
    if (
      !demo &&
      capabilities?.enabled &&
      uploadAnalysisConsent.current ===
        analysisConsentBinding(capabilities, true)
    ) {
      uploadAnalysisConsent.current = '';
      setAnalyseUploadOrigin('');
      try {
        await authFetch(
          user!,
          `/api/research/properties/${encodeURIComponent(project.id)}/analysis-jobs`,
          {
            method: 'POST',
            body: JSON.stringify({
              documentId: record.id,
              documentSha256: record.sha256,
              providerOrigin: capabilities.providerOrigin,
              transcriptionProviderOrigin:
                capabilities.transcriptionProviderOrigin ??
                capabilities.providerOrigin,
              consent: true,
              documentModel: capabilities.documentModel,
              transcriptionModel: capabilities.transcriptionModel,
            }),
          },
        );
        setMessage(
          'Datei gespeichert. Analyse und bei Aufnahmen das Transkript werden automatisch vorbereitet.',
        );
      } catch {
        setError(
          'Die Datei ist gespeichert. Der Analyseauftrag konnte nicht gestartet werden; im Analysebereich erneut versuchen.',
        );
      }
    }
  };
  const download = async (record: Document) => {
    if (!project) return;
    try {
      const blob = demo
        ? files.current.get(record.id)
        : await authFetch(
            user!,
            `/api/research/properties/${encodeURIComponent(project.id)}/due-diligence/documents/${record.id}`,
            {},
            'blob',
          );
      if (blob) saveBlob(blob, record.name);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Download fehlgeschlagen.',
      );
    }
  };
  if ((listingId && !user) || (user && isDelegated(user)))
    return (
      <AuthRequired
        title="Kaufbegleitung"
        text="Nutze die normale Haushaltsanmeldung für private Pläne und Unterlagen."
      />
    );
  const cost = costSummary(planning, project?.documents ?? []);
  const costNeedsReview = cost.unknown > 0 || !planning.finance.budgetReviewed;
  const finance = financeProjection(planning);
  const blockers = project
    ? purchaseBlockers(project.state, project.documents)
    : [];
  const visible = project
    ? checklistTemplates.filter(
        (t) => t.phase === phase && applicable(t, project.state),
      )
    : [];
  const check = visible.find((t) => t.id === checkId) ?? visible[0];
  const navigation = <aside className="journey-sidebar">
    <nav aria-label="Kaufbegleitung">{sections.map(([key, label, number]) => <button key={key}
      className={section === key ? 'active' : ''} aria-current={section === key ? 'page' : undefined}
      onClick={() => setSection(key)}><span>{number}</span>{label}</button>)}</nav>
    <div className="journey-side-note"><strong>Wissen statt Vermuten</strong><p>Ein Verkäuferwort ist ein Hinweis. Ein Befund hat eine Quelle und einen klaren Prüfbereich.</p></div>
  </aside>;
  return (
    <div className="purchase-journey">
      {demo && (
        <div className="journey-demo">
          <span>Interaktive Demo</span>Fiktive Objekte · deine Änderungen
          bleiben bis zum Neuladen in dieser Sitzung.
        </div>
      )}
      {error && (
        <p role="alert" className="journey-alert">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="journey-message">
          {message}
        </p>
      )}
      {loading ? (
        <div className="journey-empty">Deine Pläne werden geladen…</div>
      ) : !project ? (
        <>
          <header className="journey-hero">
            <div>
              <p className="eyebrow">Ein klarer nächster Schritt</p>
              <h1>Deine Immobilienpläne</h1>
              <p>
                Vergleiche Möglichkeiten. Sammle Belege. Triff Entscheidungen
                mit einem Plan, der mitlernt.
              </p>
            </div>
            <div className="journey-inline-actions">
              <Link className="button-link" to="/properties">Weiteres Objekt auswählen</Link>
              {projects.length > 0 && <a className="quiet-button" href="#gemeinsames-kapital">Gemeinsamen Kauf planen ↓</a>}
            </div>
          </header>
          <div className="journey-portfolio-summary">
            <div>
              <strong>{projects.length}</strong>
              <span>Objekte parallel</span>
            </div>
            <div>
              <strong>
                {projects.reduce(
                  (sum, p) =>
                    sum +
                    (p.state.planning?.requests.filter(
                      (r) => !['declined', 'offered'].includes(r.status),
                    ).length ?? 0),
                  0,
                )}
              </strong>
              <span>offene Finanzierungsanfragen</span>
            </div>
            <div>
              <strong>
                {projects.reduce(
                  (sum, p) =>
                    sum +
                    guidedTasks(p.state, p.documents, p.maintenance).filter(
                      (t) => t.status !== 'done',
                    ).length,
                  0,
                )}
              </strong>
              <span>nächste Schritte</span>
            </div>
          </div>
          <div className="journey-property-grid">
            {projects.map((item) => {
              const p = item.state.planning ?? newPlanning();
              const c = costSummary(p, item.documents);
              return (
                <button
                  key={item.id}
                  className="journey-property-card"
                  onClick={() => {
                    setSelectedId(item.id);
                    setSection('overview');
                  }}
                >
                  <div className="journey-house-mark">⌂</div>
                  <span className="journey-stage">
                    {stageLabels[item.state.stage]}
                  </span>
                  <h2>{item.title}</h2>
                  <p>{item.location}</p>
                  <div>
                    <span>Kaufpreis im Plan</span>
                    <strong>{eur(p.finance.purchasePrice)}</strong>
                  </div>
                  <div>
                    <span>Zusätzliche Prüfungen & Arbeiten</span>
                    <strong>
                      {costRangeLabel(c, Boolean(p.finance.budgetReviewed))}
                    </strong>
                  </div>
                  <small>
                    {c.unknown
                      ? `${c.unknown} Kostenpositionen noch offen`
                      : !p.finance.budgetReviewed
                        ? 'Kostenplanung noch prüfen'
                        : 'Bekannte Kosten im Plan'}{' '}
                    · {item.documents.length} Unterlagen
                  </small>
                  <b>Plan öffnen →</b>
                </button>
              );
            })}
          </div>
          {!projects.length && (
            <div className="journey-empty">
              <h2>Dein erstes Objekt wartet.</h2>
              <p>
                Öffne ein Objekt aus deiner Liste und starte die Kaufbegleitung.
                Weitere Pläne kannst du parallel vergleichen.
              </p>
              <Link to="/properties">Zur Objektliste →</Link>
            </div>
          )}
        </>
      ) : (
        <>
          <header className="journey-project-header">
            <div>
              <button
                className="journey-back"
                onClick={() => {
                  setSelectedId('');
                  setSection('overview');
                }}
              >
                ← Alle Pläne
              </button>
              <h1>{project.title}</h1>
              <p>
                {project.location} · {stageLabels[project.state.stage]}
              </p>
            </div>
            <div className="journey-inline-actions">
              {!demo && user && project.version>0 && project.householdId && project.actorId && <button className="quiet-button" onClick={()=>setHistoryId(project.id)}>Gespeicherte Planstände</button>}
              {!demo && user && !isDelegated(user) && project.version>0 && project.householdId && project.actorId && <button type="button" className="quiet-button" onClick={()=>setImportId(project.id)}>Planexport importieren</button>}
              {!demo && user && !isDelegated(user) && project.version>0 && project.householdId && project.actorId && <button type="button" className="quiet-button" onClick={()=>setArchiveId(project.id)}>Unterlagenarchiv wiederherstellen</button>}
              <button
                className="quiet-button"
                onClick={() =>
                  saveBlob(
                    new Blob([JSON.stringify(project, null, 2)], {
                      type: 'application/json',
                    }),
                    'immobilienplan.json',
                  )
                }
              >
                Plan exportieren
              </button>
              {!demo && user && !isDelegated(user) && <button className="quiet-button"
                disabled={Boolean(exportingId) || removalPendingIds.includes(project.id) || dirtyIds.includes(project.id) || (project.version === 0 && project.documents.length === 0)}
                title={dirtyIds.includes(project.id) ? 'Änderungen zuerst speichern; das Archiv enthält den gespeicherten Stand.' : 'Gespeicherter Plan, Unterlagen und Prüfverlauf als Archiv.'}
                onClick={async () => {
                  setExportingId(project.id); setError('');
                  try {
                    await exportEvidence(user,project.id,project.version,()=>setMessage('Unterlagenexport gestartet. Der Browser zeigt Fortschritt und Abbruch des Downloads.'));
                    setMessage('Das Archiv wurde an den Browser übergeben. Bitte den fertigen Download dort prüfen.');
                  } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unterlagenexport fehlgeschlagen.'); }
                  finally { setExportingId(''); }
                }}>{exportingId === project.id ? 'Unterlagen werden exportiert…' : 'Mit Unterlagen exportieren'}</button>}
              {!demo && (
                <button
                  disabled={
                    busy || removalPendingIds.includes(project.id) || archivePendingIds.includes(project.id) ||
                    (project.version > 0 && !dirtyIds.includes(project.id))
                  }
                  onClick={() => void save()}
                >
                  {busy
                    ? 'Speichert…'
                    : dirtyIds.includes(project.id)
                      ? 'Änderungen speichern'
                      : project.version === 0
                        ? 'Plan speichern'
                        : 'Gespeichert'}
                </button>
              )}
            </div>
          </header>
          {user&&!isDelegated(user)&&project.householdId&&project.actorId&&<AccountPropertyHistory key={`account-history:${project.id}:${project.version}:${project.householdId}:${project.actorId}`} user={user}
            scope={{householdId:project.householdId,actorId:project.actorId,listingId:project.id,currentVersion:project.version}}
            blocked={busy||dirtyIds.includes(project.id)||maintenanceDirtyIds.includes(project.id)||removalPendingIds.includes(project.id)||archivePendingIds.includes(project.id)} onDocument={document=>void download(document)}
            onLoadPlan={v=>{if(!v.draft?.state||v.target?.listingId!==project.id||v.target.version!==project.version||v.householdId!==project.householdId||v.actorId!==project.actorId||busy||dirtyIds.includes(project.id)||maintenanceDirtyIds.includes(project.id)||removalPendingIds.includes(project.id)||archivePendingIds.includes(project.id))return;update({...project,state:v.draft.state});setSection('overview');setMessage('Kontoarchivplan als ungeprüfter Entwurf geladen. Geöffnete Prüfungen und ersetzte Kosten kontrollieren; danach gesondert speichern.');}}
            onLoadMaintenance={d=>{if(d.listingId!==project.id||d.actorId!==project.actorId||d.householdId!==project.householdId||busy||dirtyIds.includes(project.id)||maintenanceDirtyIds.includes(project.id)||removalPendingIds.includes(project.id)||archivePendingIds.includes(project.id))return;setArchiveMaintenanceDraft(d);setSection('ownership');}}/>}
          {archiveId===project.id && user && !isDelegated(user) && project.householdId && project.actorId && <ArchiveImportPanel
            key={`archive:${project.id}:${project.householdId}:${project.actorId}`} user={user}
            scope={{householdId:project.householdId,actorId:project.actorId,listingId:project.id,currentVersion:project.version}}
            blocked={busy||dirtyIds.includes(project.id)||maintenanceDirtyIds.includes(project.id)||removalPendingIds.includes(project.id)}
            onClose={()=>setArchiveId('')} onPendingChange={archivePending} onDocument={document=>void download(document)}
            onRestored={async()=>{
              const data=await authFetch(user,`/api/research/properties/${encodeURIComponent(project.id)}/due-diligence`);
              if(removalSession.current!==user||data.householdId!==project.householdId||data.actorId!==project.actorId)throw new Error('Anmeldung hat gewechselt. Ergebnis über den ursprünglichen Auftrag klären.');
              setProjects(items=>items.map(item=>item.id===project.id?{...item,documents:data.documents}:item));
            }} onLoadPlan={p=>{
              if(!p.draft?.state||busy||dirtyIds.includes(project.id)||maintenanceDirtyIds.includes(project.id)||removalPendingIds.includes(project.id)||p.dossierVersion!==project.version||p.householdId!==project.householdId||p.actorId!==project.actorId)return;
              update({...project,state:p.draft.state});setArchiveId('');setSection('overview');setMessage('Archivplan als ungeprüfter Entwurf geladen. Quellen und ersetzte Kosten prüfen; danach gesondert speichern.');
            }} onLoadMaintenance={d=>{
              if(busy||dirtyIds.includes(project.id)||maintenanceDirtyIds.includes(project.id)||removalPendingIds.includes(project.id)||d.householdId!==project.householdId||d.actorId!==project.actorId||d.listingId!==project.id)return;
              setArchiveMaintenanceDraft(d);setArchiveId('');setSection('ownership');
            }}/>}
          {importId===project.id && user && !isDelegated(user) && project.householdId && project.actorId && <PlanImportPanel
            key={`import:${project.id}:${project.version}:${project.householdId}:${project.actorId}`} user={user}
            scope={{householdId:project.householdId,actorId:project.actorId,listingId:project.id,currentVersion:project.version}}
            blocked={busy||dirtyIds.includes(project.id)||maintenanceDirtyIds.includes(project.id)||removalPendingIds.includes(project.id)||archivePendingIds.includes(project.id)}
            onClose={()=>setImportId('')} onLoadDraft={preview=>{
              if(!preview.draft||busy||dirtyIds.includes(project.id)||maintenanceDirtyIds.includes(project.id)||removalPendingIds.includes(project.id)
                ||preview.currentVersion!==project.version||preview.householdId!==project.householdId||preview.actorId!==project.actorId||preview.listingId!==project.id)return;
              update({...project,state:preview.draft});setImportId('');setSection('overview');setMessage('Planexport als ungeprüfter Entwurf geladen. Kostenumfang und geöffnete Annahmen prüfen; danach bewusst speichern.');
            }}/>}
          {historyId===project.id && user && project.householdId && project.actorId && <PropertyHistoryPanel
            key={`${project.id}:${project.version}:${project.householdId}:${project.actorId}`}
            user={user} scope={{householdId:project.householdId,actorId:project.actorId,listingId:project.id,currentVersion:project.version}}
            blocked={busy||dirtyIds.includes(project.id)||maintenanceDirtyIds.includes(project.id)||removalPendingIds.includes(project.id)}
            onClose={()=>setHistoryId('')} onLoadDraft={detail=>{
              if (!detail.draft||busy||dirtyIds.includes(project.id)||maintenanceDirtyIds.includes(project.id)||removalPendingIds.includes(project.id)
                ||detail.currentVersion!==project.version||detail.householdId!==project.householdId||detail.actorId!==project.actorId||detail.listingId!==project.id) return;
              update({...project,state:detail.draft});setHistoryId('');setSection('overview');setMessage(`Planstand ${detail.version} als neuer Entwurf geladen. Prüfe die geöffneten Annahmen und speichere danach bewusst.`);
            }} />}
          <div className="journey-project-layout" hidden={section === 'ownership'}>
            {navigation}
            <fieldset disabled={busy || removalPendingIds.includes(project.id)} className="journey-content">
              {section === 'overview' && (
                <>
                  <div
                    className={`journey-next-step${firstIssue ? ' has-findings' : ''}`}
                  >
                    <p className="eyebrow">Dein nächster guter Schritt</p>
                    <h2>
                      {firstIssue
                        ? 'Kläre die gemeldeten Probleme zuerst.'
                        : !project.documents.length
                        ? 'Bringe die ersten Unterlagen zusammen.'
                        : costNeedsReview
                          ? cost.unknown ? 'Kläre die noch offenen Kosten.' : 'Prüfe, ob der Kostenumfang vollständig ist.'
                          : !planning.requests.length
                            ? 'Starte die Finanzierungsgespräche.'
                            : blockers.length
                              ? 'Klärt die offenen Fachprüfungen.'
                              : 'Bereitet eure Entscheidung vor.'}
                    </h2>
                    <p>
                      {firstIssue
                        ? <>
                            {issues.length === 1
                              ? 'Eine Fachprüfung steht'
                              : `${issues.length} Fachprüfungen stehen`}{' '}
                            auf „Widerspruch / Mangel“. Nächster Schritt:{' '}
                            <strong>{firstIssue.title}</strong> Ergänzt den
                            passenden schriftlichen Befund; ein allgemeines
                            Gesamturteil ersetzt die einzelne Fachprüfung nicht.
                          </>
                        : !project.documents.length
                        ? 'Exposé, Pläne und Fachberichte schaffen die Grundlage. Danach ordnest du Aussagen ihren Belegen zu.'
                        : costNeedsReview
                          ? cost.unknown ? 'Eine offene Position ist kein Null-Euro-Risiko. Hole Befunde und Angebote ein und ergänze eine belastbare Spanne.'
                            : 'Gehe Fachprüfungen, Arbeiten und Reserven bewusst durch. Erst danach ist ein leerer Kostenplan ein geplanter Null-Euro-Betrag.'
                          : 'Alles bleibt mit diesem Objekt verbunden: Antworten, Dokumente, Angebote und nächste Aufgaben.'}
                    </p>
                    <button
                      onClick={() =>
                        firstIssue ? openTaskSource(firstIssue) : setSection(
                          !project.documents.length
                            ? 'documents'
                            : costNeedsReview
                              ? 'costs'
                              : !planning.requests.length
                                ? 'finance'
                                : 'checks',
                        )
                      }
                    >
                      {firstIssue
                        ? 'Prüfung & Belege klären →'
                        : 'Nächsten Schritt öffnen →'}
                    </button>
                  </div>
                  <div className="journey-metrics">
                    <div>
                      <span>Arbeiten & Prüfungen</span>
                      <strong>
                        {costRangeLabel(cost, Boolean(planning.finance.budgetReviewed))}
                      </strong>
                      <small>
                        {!planning.finance.budgetReviewed
                          ? 'Kostenumfang noch zu prüfen'
                          : `${cost.unknown} Positionen noch offen`}
                      </small>
                    </div>
                    <div>
                      <span>Finanzierung monatlich</span>
                      <strong>{eur(finance.payment)}</strong>
                      <small>
                        {!finance.complete
                          ? 'Vorläufiger Teilplan'
                          : planning.finance.financingMode === 'combined'
                            ? 'Gewählte Darlehensbausteine'
                            : planning.finance.selectedRequestId
                              ? 'Gewählte Konditionen'
                              : 'Vorläufige Annahmen'}
                      </small>
                    </div>
                    <div>
                      <span>
                        {project.state.stage === 'ownership'
                          ? 'Offene Fachfragen'
                          : 'Vor der Entscheidung'}
                      </span>
                      <strong>{blockers.length} Fragen</strong>
                      <small>Fachprüfungen getrennt klären</small>
                    </div>
                  </div>
                  <div className="journey-overview-grid">
                    <section className="journey-panel">
                      <h3>Was als Nächstes ansteht</h3>
                      {tasks
                        .filter((t) => t.status !== 'done')
                        .slice(0, 4)
                        .map((task) => (
                          <button
                            className="journey-next-row"
                            key={task.id}
                            onClick={() => setSection('tasks')}
                          >
                            <span>○</span>
                            <div>
                              <strong>{task.title}</strong>
                              <small>
                                {task.owner || 'Verantwortung offen'} ·{' '}
                                {task.dueDate || 'Termin offen'}
                              </small>
                            </div>
                            <b>→</b>
                          </button>
                        ))}
                      <button
                        className="quiet-button"
                        onClick={() => setSection('tasks')}
                      >
                        Aufgaben planen
                      </button>
                    </section>
                    <section className="journey-panel">
                      <h3>Welche Kosten haben schon einen Beleg?</h3>
                      <div className="journey-confidence" role="img" aria-label={`${cost.documented} von ${cost.total} Positionen mit verknüpftem Angebot oder Rechnung`}>
                        <div style={{ width: `${cost.documentCoverage * 100}%` }} />
                      </div>
                      <p>
                        {cost.documented} von {cost.total} Positionen mit
                        verknüpftem Angebot oder Rechnung.
                      </p>
                      <p className="journey-caption">
                        Die Anzeige zählt verfügbare Dateien mit Fundstelle.
                        Umfang und Ausschlüsse prüfst du im Original; frühere
                        Kostenschätzungen bleiben erhalten.
                      </p>
                      <button
                        className="quiet-button"
                        onClick={() => setSection('costs')}
                      >
                        Kostenstände ansehen
                      </button>
                    </section>
                  </div>
                  <section className="journey-panel">
                    <h3>Der Weg über den Kauf hinaus</h3>
                    <div className="journey-lifecycle">
                      {Object.entries(stageLabels).map(
                        ([key, label], index) => (
                          <div
                            key={key}
                            className={
                              project.state.stage === key ? 'active' : ''
                            }
                          >
                            <span>{index + 1}</span>
                            <strong>{label}</strong>
                          </div>
                        ),
                      )}
                    </div>
                    <p className="journey-caption">
                      Übergabe und Wartung führen denselben Plan weiter. Ein
                      abgeschlossener Prüfpunkt ist keine pauschale
                      Kauf-Freigabe.
                    </p>
                  </section>
                </>
              )}
              {section === 'tasks' && (
                <TaskPanel
                  planning={planning}
                  documents={project.documents}
                  title={project.title}
                  onChange={plan}
                  tasks={tasks}
                  focusTaskId={reminderTask}
                  stage={project.state.stage}
                  onLinkedChange={(task) =>
                    update({
                      ...project,
                      state: updateGuidedTask(project.state, task),
                    })
                  }
                  onOpenSource={openTaskSource}
                />
              )}
              <div hidden={section !== 'costs'} key={project.id}>
                <CostsPanel
                  planning={planning}
                  documents={project.documents}
                  onChange={plan}
                  onOpenDocuments={() => setSection('documents')}
                />
              </div>
              {section === 'finance' && (
                <FinancingPanel
                  planning={planning}
                  documents={project.documents}
                  onChange={plan}
                />
              )}
              {section === 'documents' && (
                <section className="journey-panel">
                  <div className="journey-section-heading">
                    <div>
                      <h2>Alles, was ihr herausgefunden habt</h2>
                      <p>
                        Unterlagen, Fachberichte und Besuche bleiben bei diesem
                        Objekt. Jede Fassung hat ihre eigene Quelle.
                      </p>
                    </div>
                  </div>
                  {!demo && capabilities?.enabled && (
                    <label className="journey-checkbox">
                      <input
                        type="checkbox"
                        checked={
                          analyseUploadOrigin ===
                          analysisConsentBinding(capabilities, true)
                        }
                        onChange={(event) => {
                          const binding = event.target.checked
                            ? analysisConsentBinding(capabilities, true)
                            : '';
                          uploadAnalysisConsent.current = binding;
                          setAnalyseUploadOrigin(binding);
                        }}
                      />
                      Die nächste hochgeladene Datei darf automatisch durch{' '}
                      {analysisServiceLabel(capabilities, true)} analysiert
                      werden. Bei einer Aufnahme sind die Einwilligungen der
                      Beteiligten geklärt.
                    </label>
                  )}
                  <label className="journey-upload">
                    <strong>Dokument hinzufügen</strong>
                    <span>
                      {demo
                        ? 'Dokument, Foto oder Besuchsaufnahme · Demo bis 100 MB'
                        : 'Dokument oder Foto · bis 100 MB'}
                    </span>
                    <input
                      aria-label="Dokument hochladen"
                      type="file"
                      disabled={busy}
                      accept={
                        demo
                          ? 'application/pdf,image/png,image/jpeg,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,audio/*,video/mp4,video/quicktime,video/webm'
                          : 'application/pdf,image/png,image/jpeg,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                      }
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) void upload(file);
                        event.target.value = '';
                      }}
                    />
                  </label>
                  {!demo && user && (
                    <RecordingUploader
                      key={project.id}
                      user={user}
                      projectId={project.id}
                      onStored={storedDocument}
                    />
                  )}
                  <div className="journey-document-list">
                    {project.documents.map((document) => (
                      <article key={document.id}>
                        <div className="journey-file-icon">↗</div>
                        <div>
                          <strong>{document.name}</strong>
                          <small>
                            {fileSize(document.size)} · Original mit SHA256{' '}
                            {document.sha256.slice(0, 12)}…
                          </small>
                        </div>
                        <button
                          className="quiet-button"
                          onClick={() => void download(document)}
                        >
                          Herunterladen
                        </button>
                        {!demo && user && !isDelegated(user) && <button type="button" className="quiet-button"
                          disabled={busy || dirtyIds.includes(project.id) || maintenanceDirtyIds.includes(project.id) || removalPendingIds.includes(project.id)}
                          onClick={() => setRemovalTarget(document)}>Datei entfernen…</button>}
                      </article>
                    ))}
                  </div>
                  {!project.documents.length && (
                    <p className="journey-caption">
                      Noch keine Unterlagen. Nach dem Hochladen verbindest du
                      den genauen Befund mit einer Prüfung oder Kostenposition.
                    </p>
                  )}
                  <AnalysisPanel
                    user={demo ? null : user}
                    projectId={project.id}
                    documents={project.documents}
                    planning={planning}
                    version={project.version}
                    dirty={dirtyIds.includes(project.id)}
                    capabilities={capabilities}
                    onAccepted={async () => {
                      if (!user) return;
                      const dossier = await authFetch(
                        user,
                        `/api/research/properties/${encodeURIComponent(project.id)}/due-diligence`,
                      );
                      setProjects((current) =>
                        current.map((item) =>
                          item.id === project.id
                            ? {
                                ...item,
                                state: dossier.state,
                                documents: dossier.documents,
                                version: dossier.version,
                              }
                            : item,
                        ),
                      );
                      setMessage(
                        'Neue Angaben wurden mit Belegen übernommen. Betroffene Prüfungen erneut klären.',
                      );
                    }}
                  />
                </section>
              )}
              {section === 'checks' && (
                <div className="journey-check-layout">
                  <section className="journey-panel">
                    <h2>Prüfen & entscheiden</h2>
                    <p>
                      {blockers.length} Fachfragen vor der Notarvorbereitung
                      offen.
                    </p>
                    <div className="journey-form-grid">
                      {(
                        [
                          ['oilHeating', 'Ölheizung / Tank'],
                          ['basement', 'Keller'],
                          ['alterations', 'Anbau / geänderte Nutzung'],
                        ] as const
                      ).map(([key, label]) => (
                        <label key={key}>
                          {label}
                          <select
                            aria-label={label}
                            value={project.state.profile[key]}
                            onChange={(event) =>
                              update({
                                ...project,
                                state: {
                                  ...project.state,
                                  profile: {
                                    ...project.state.profile,
                                    [key]: event.target.value as
                                      | 'yes'
                                      | 'no'
                                      | 'unknown',
                                  },
                                },
                              })
                            }
                          >
                            <option value="unknown">Ungeklärt</option>
                            <option value="yes">Vorhanden</option>
                            <option value="no">Nicht vorhanden</option>
                          </select>
                        </label>
                      ))}
                    </div>
                    <nav className="journey-check-phases">
                      {[
                        ['purchase', 'Vor dem Kauf'],
                        ['handover', 'Übergabe'],
                        ['ownership', 'Wartung'],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          className={phase === key ? '' : 'quiet-button'}
                          onClick={() => setPhase(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </nav>
                    {visible.map((template) => (
                      <button
                        className={`journey-check-choice ${check?.id === template.id ? 'active' : ''}`}
                        key={template.id}
                        onClick={() => setCheckId(template.id)}
                      >
                        <strong>{template.title}</strong>
                        <span>
                          {isClosed(
                            template,
                            project.state.checks[template.id],
                            project.documents,
                          )
                            ? '✓ ' +
                              statusLabels[
                                project.state.checks[template.id].status
                              ]
                            : statusLabels[
                                project.state.checks[template.id].status
                              ]}
                        </span>
                      </button>
                    ))}
                    <label>
                      Aktuelle Phase
                      <select
                        aria-label="Aktuelle Phase"
                        value={project.state.stage}
                        onChange={(event) => {
                          const stage = event.target.value as Dossier['stage'];
                          if (
                            blockers.length &&
                            ['notary', 'handover'].includes(stage)
                          ) {
                            setError(
                              'Erst offene Kaufprüfungen klären, bevor ihr zur Notarvorbereitung weitergeht.',
                            );
                            return;
                          }
                          update({
                            ...project,
                            state: { ...project.state, stage },
                          });
                        }}
                      >
                        {Object.entries(stageLabels).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </section>
                  {check && (
                    <CheckEditor
                      key={`${project.id}:${check.id}`}
                      template={check}
                      check={project.state.checks[check.id]}
                      documents={project.documents}
                      onChange={(value) =>
                        update({
                          ...project,
                          state: {
                            ...project.state,
                            checks: {
                              ...project.state.checks,
                              [check.id]: value,
                            },
                          },
                        })
                      }
                    />
                  )}
                </div>
              )}
            </fieldset>
            {!demo && user && !isDelegated(user) && <DocumentRemovalPanel key={`${user.profile.sub}:${project.id}`} user={user} projectId={project.id} version={project.version}
              target={removalTarget && project.documents.some(document => document.id === removalTarget.id) ? removalTarget : null}
              blocked={loading || busy || dirtyIds.includes(project.id) || maintenanceDirtyIds.includes(project.id)} hidden={section !== 'documents'}
              onClose={() => setRemovalTarget(null)} onPendingChange={removalPending} onReload={reloadAfterRemoval} />}
          </div>
        </>
      )}
      <PortfolioFundingPanel key={user?.profile.sub ?? 'demo'} user={user} projects={projects} dirtyIds={[...dirtyIds, ...maintenanceDirtyIds, ...removalPendingIds,...archivePendingIds]}
        hidden={Boolean(project) || loading} onReloadProjects={() => user && setPortfolioReload(value => value + 1)}
        onOpenProject={id => { setSelectedId(id); setSection('finance'); }} onOpenMaintenance={id => { setSelectedId(id); setSection('ownership'); }} />
      <MaintenanceWorkspace key={`maintenance:${user?.profile.sub ?? 'demo'}`} user={user} projects={projects}
        navigation={navigation}
        activeId={!loading && section === 'ownership' ? project?.id ?? '' : ''} focusId={maintenanceFocus}
        onDirtyChange={maintenanceDirty} blockedIds={[...removalPendingIds,...archivePendingIds]} refreshKeys={maintenanceRefreshKeys}
        archiveDraft={archiveMaintenanceDraft} onArchiveDraftDismiss={clearArchiveMaintenance}
        onSnapshot={maintenanceSnapshot} onDocument={document => void download(document)} onCosts={() => setSection('costs')} />
    </div>
  );
}
